import React, { useState, useEffect } from 'react';
import axios from "axios";

const committeeMeetingCache = new Map();
const CACHE_DURATION = 300000;

function getCachedData(key) {
    const cachedData = committeeMeetingCache.get(key);
    if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
        return cachedData.data;
    }
    return null;
}

function setCachedData(key, data) {
    committeeMeetingCache.set(key, { data, timestamp: Date.now() });
}

export default function CommitteeHearingDate({
    billId,
    billStatusCode,
    statusDate,
    meetingIndex = 0 // Default to 0 if not provided
}) {
    const [committeeMeeting, setCommitteeMeeting] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    function formatDate(dateString) {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";

        const months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
        const month = months[date.getMonth()];
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'pm' : 'am';

        hours = hours % 12;
        hours = hours ? hours : 12;
        hours = hours.toString().padStart(2, '0');

        return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
    }

    async function fetchStandingCommitteeMeetings(billId) {
        const cacheKey = `findByBillId-${billId}`;
        const cachedData = await getCachedData(cacheKey);
        if (cachedData) {
            return cachedData;
        }
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/standingCommitteeMeetingBillHearings/findByBillId?billId=${billId}`,
            );
            const sortedMeetings = response.data.sort((a, b) => {
                return new Date(b.committeeMeeting?.meetingTime) - new Date(a.committeeMeeting?.meetingTime);
            });
            setCachedData(cacheKey, sortedMeetings);
            return sortedMeetings;
        } catch (error) {
            console.error("Error fetching committee meeting data: ", error);
            throw error;
        }
    }

    useEffect(() => {
        async function loadCommitteeMeeting() {
            if (!billId) {
                setCommitteeMeeting(null);
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null); // Reset error state at the start

            try {
                const cacheKey = `findByBillId-${billId}`;
                const cachedData = getCachedData(cacheKey);
                let data;
                if (cachedData) {
                    data = cachedData;
                } else {
                    data = await fetchStandingCommitteeMeetings(billId);
                }

                if (!data || data.length === 0) {
                    setCommitteeMeeting(null);
                    return;
                }

                // Use the provided meetingIndex, but ensure it's within bounds
                const index = Math.min(Math.max(0, meetingIndex), data.length - 1);
                const currentMeeting = data[index];

                if (currentMeeting && currentMeeting.committeeMeeting) {
                    const formattedMeetingTime = formatDate(currentMeeting.committeeMeeting.meetingTime);
                    setCommitteeMeeting({
                        ...currentMeeting,
                        formattedMeetingTime,
                    });
                } else {
                    setCommitteeMeeting(null);
                }
            } catch (err) {
                console.error("Error loading committee meeting:", err);
                setError({ message: "Unable to load hearing details" });
            } finally {
                setLoading(false);
            }
        }
        loadCommitteeMeeting();
    }, [billId, meetingIndex]); // Add meetingIndex to dependencies

    if (loading) return <span></span>;
    if (error) return <span>{error.message}</span>; // Remove "Error:" prefix
    if (!committeeMeeting) return null;

    const isHearing = billStatusCode && billStatusCode.name &&
        (billStatusCode.name.toLowerCase().includes("hearing"));

    if (!isHearing) return null;

    return (
        <>
            {committeeMeeting?.formattedMeetingTime || 'Date not available'}
            , {committeeMeeting?.committeeMeeting?.location ? committeeMeeting.committeeMeeting.location : 'Location not available'}
            {committeeMeeting?.status === "CANCELED" ? (
                <strong> - {committeeMeeting?.status || ""}</strong>
            ) : null}
        </>
    );
}
